import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listPimPowerwear } from "../lib/api.js";
import type { PimPowerwearItem } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PriceDisplay } from "../ui/PriceDisplay.js";
import { PackageIcon, HashIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * PowerWear katalog — oblečení / Garments.
 *
 * Filtrace: velikost, barva, gender, fulltext, skladem.
 * Jeden SKU = jedna varianta (velikost × barva). Pro grupování
 * slouží `variantGroup` (sdílený popis/obrázek napříč velikostmi).
 */
export function PowerWearPage(): JSX.Element {
  const [search, setSearch] = useState("");
  const [size, setSize] = useState<string | null>(null);
  const [colour, setColour] = useState<string | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [hasStock, setHasStock] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);

  const [items, setItems] = useState<PimPowerwearItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, size, colour, gender, hasStock, showDiscontinued]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPimPowerwear({
      search: debouncedSearch || undefined,
      size: size ?? undefined,
      colour: colour ?? undefined,
      gender: gender ?? undefined,
      hasStock,
      includeDiscontinued: showDiscontinued,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, size, colour, gender, hasStock, showDiscontinued, offset]);

  // Z aktuálních výsledků odvodíme dostupné filtry (jednoduché — bez API aggregation).
  const availableSizes = useMemo(
    () => unique(items?.map((i) => i.size) ?? []),
    [items],
  );
  const availableColours = useMemo(
    () => unique(items?.map((i) => i.colour) ?? []),
    [items],
  );
  const availableGenders = useMemo(
    () => unique(items?.map((i) => i.gender) ?? []),
    [items],
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          PowerWear
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Textilní kolekce KTM / Husqvarna / GasGas / WP — Casual, Functional,
          Merchandise.
        </p>
      </header>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat číslo nebo název…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-72"
          />
        </div>

        {availableGenders.length > 0 && (
          <FilterSelect
            label="Gender"
            value={gender}
            options={availableGenders}
            onChange={setGender}
          />
        )}
        {availableSizes.length > 0 && (
          <FilterSelect
            label="Velikost"
            value={size}
            options={availableSizes}
            onChange={setSize}
          />
        )}
        {availableColours.length > 0 && (
          <FilterSelect
            label="Barva"
            value={colour}
            options={availableColours}
            onChange={setColour}
          />
        )}

        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={hasStock}
            onChange={(e) => setHasStock(e.target.checked)}
          />
          Jen skladem ve Vlašimi
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={showDiscontinued}
            onChange={(e) => setShowDiscontinued(e.target.checked)}
          />
          Zobrazit i zrušené
        </label>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {/* Grid */}
      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné výsledky"
          description="Upravte filtr nebo hledání."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <PowerWearCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function PowerWearCard({ item }: { item: PimPowerwearItem }): JSX.Element {
  const isDiscontinued = item.articleStatus === "80";
  const isCollection = item.pimCategory === "powerwear_collection";

  return (
    <Link
      to={`/part/${encodeURIComponent(item.articleNumber)}`}
      className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name ?? ""}
            className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        {isCollection && (
          <div className="absolute top-2 left-2">
            <Badge tone="blue">Collection</Badge>
          </div>
        )}
        {isDiscontinued && (
          <div className="absolute top-2 right-2">
            <Badge tone="red">Zrušeno</Badge>
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono">{item.articleNumber}</div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name ?? "Bez názvu"}
        </div>
        <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-zinc-500">
          {item.gender && <span>{item.gender}</span>}
          {item.size && (
            <>
              {item.gender && <span className="text-zinc-300">·</span>}
              <span>vel. {item.size}</span>
            </>
          )}
          {item.colour && (
            <>
              {(item.gender || item.size) && <span className="text-zinc-300">·</span>}
              <span>{item.colour}</span>
            </>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            {item.price ? (
              <PriceDisplay price={item.price} size="sm" />
            ) : (
              <span className="text-xs text-zinc-400">Bez ceny</span>
            )}
          </div>
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
}): JSX.Element {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white"
    >
      <option value="">{label}: vše</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function unique<T>(arr: Array<T | null>): T[] {
  const set = new Set<T>();
  for (const v of arr) if (v != null) set.add(v);
  return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
}
